import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge, Box, Button, Flex, HStack, IconButton, Input, InputGroup, InputLeftElement, Spinner, Text, useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FiChevronLeft, FiMaximize2, FiMessageCircle, FiMinus, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import {
  ADMIN_ERROR_TEXT, ADMIN_QUICK_REPLIES, OPS_ORIGIN_LABEL, adminRoomName, adminUnreadTotal, errorCodeOf, isAdminOpsChatPath,
  latestUnreadRow, sendMessage,
} from "../../../lib/opsChat";
import { fmtDateTime } from "../../../lib/formatLocale";
import { useAdminOpsRoom, useAdminOpsRoomList } from "../../../hooks/useAdminOpsChat";
import AdminOpsRoomRow from "../../opsChat/AdminOpsRoomRow";
import OpsChatMessages from "../../opsChat/OpsChatMessages";
import OpsChatComposer from "../../opsChat/OpsChatComposer";
import AdminFeatureTip from "../featureNotice/AdminFeatureTip";

/**
 * 어드민 어디서나 오른쪽 아래에 떠 있는 운영팀 대화창 (2026-11-03 · 시안 A https://claude.ai/artifact/AoWKMrjNrgNSJ5Sn58FQVx)
 *
 *   · 어드민 레이아웃에 한 번 붙어 있어 다른 메뉴로 옮겨도 창·고른 방·쓰던 글이 그대로 남는다.
 *   · 버튼 숫자 = 열린 방들의 안 읽은 크리에이터 메시지 합. 새 답장이 오면 버튼 옆 말풍선(8초).
 *   · 읽기·답장·첨부만. 방 열기·종료·담당자 바꾸기·회원 정보는 「크게 보기」(대화방 관리 화면)에서.
 *   · 「대화방 관리」 화면에서는 숨긴다. 대화방 기능이 꺼져 있거나 권한이 없으면 아무것도 그리지 않는다.
 *   · 표는 직접 읽지 않고 RPC 로만 — 훅 src/hooks/useAdminOpsChat.js 가 대화방 관리 화면과 같은 규칙으로 읽는다.
 */

const STORE_KEY = "laeebly:admin-ops-dock";
const BUBBLE_MS = 8000;
const FILTERS = [["open", "열림"], ["mine", "내 담당만"], ["unread", "안 읽음만"]];
const HIDE_ON_ERROR = new Set(["forbidden", "disabled"]);
const errText = (err) => ADMIN_ERROR_TEXT[errorCodeOf(err)] || ADMIN_ERROR_TEXT.unknown;

function readStore() {
  try { return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || "null") || {}; } catch { return {}; }
}
function writeStore(v) {
  try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* 저장 안 돼도 화면은 돈다 */ }
}

export function filterDockRows(rows, { filter, q, me }) {
  const needle = String(q || "").trim().toLowerCase();
  const mine = String(me || "").toLowerCase();
  return (rows || []).filter((r) => {
    if (filter === "mine" && String(r.assignee_email || "").toLowerCase() !== mine) return false;
    if (filter === "unread" && !(Number(r.unread) > 0)) return false;
    if (!needle) return true;
    return [r.creator_email, r.creator_name, r.creator_nick].some((v) => String(v || "").toLowerCase().includes(needle));
  });
}

export default function AdminOpsChatDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const saved = useMemo(readStore, []);
  const [open, setOpen] = useState(Boolean(saved.open));
  const [room, setRoom] = useState(saved.roomId ? { id: saved.roomId, name: saved.roomName || "" } : null);
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [bubble, setBubble] = useState(null);
  const prevTotal = useRef(null);
  const buttonRef = useRef(null);

  const onPage = isAdminOpsChatPath(location.pathname);
  const { rows, loading, error, reload } = useAdminOpsRoomList({ filter: "open" });
  const hidden = onPage || (error && HIDE_ON_ERROR.has(errorCodeOf(error)));

  useEffect(() => { writeStore({ open, roomId: room?.id || null, roomName: room?.name || "" }); }, [open, room]);

  const total = adminUnreadTotal(rows);
  useEffect(() => {
    if (loading) return;
    if (prevTotal.current !== null && total > prevTotal.current && !open) {
      const r = latestUnreadRow(rows);
      if (r) setBubble({ id: r.id, name: adminRoomName(r), text: r.last_preview || (Number(r.last_attachment_count) ? `첨부 ${r.last_attachment_count}개` : "") });
    }
    if (total === 0) setBubble(null);
    prevTotal.current = total;
  }, [total, rows, loading, open]);
  useEffect(() => {
    if (!bubble) return undefined;
    const t = setTimeout(() => setBubble(null), BUBBLE_MS);
    return () => clearTimeout(t);
  }, [bubble]);

  const collapse = useCallback(() => {
    setOpen(false);
    setTimeout(() => buttonRef.current?.focus?.(), 0);
  }, []);
  const openRoom = (r) => { setRoom({ id: r.id, name: r.name || adminRoomName(r) }); setOpen(true); setBubble(null); };

  if (hidden) return null;

  const visibleRows = filterDockRows(rows, { filter, q, me: userInfo?.email });
  const unreadElsewhere = room ? adminUnreadTotal(rows.filter((r) => r.id !== room.id)) : 0;

  return (
    <>
      {open && (
        <Flex
          as="section"
          role="dialog"
          aria-label={room ? `운영팀 대화방 — ${room.name}` : "운영팀 대화방"}
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); collapse(); } }}
          position="fixed" right={{ base: "16px", md: "20px" }} bottom={{ base: "80px", md: "84px" }}
          w={{ base: "calc(100vw - 32px)", md: "380px" }} h="600px" maxH="calc(100vh - 140px)"
          bg="white" border="1px solid" borderColor="neutral.200" borderRadius="14px" boxShadow="0 12px 36px rgba(26,25,24,0.18)"
          direction="column" overflow="hidden" zIndex={1250}
        >
          {room
            ? <DockRoom key={room.id} room={room} unreadElsewhere={unreadElsewhere}
                onBack={() => { setRoom(null); reload({ quiet: true }); }}
                onBig={() => navigate(`/admin/inquiries/chats/${room.id}`)}
                onCollapse={collapse} onChanged={() => reload({ quiet: true })} />
            : (
              <>
                <Flex px={4} py={2.5} align="center" gap={1} borderBottom="1px solid" borderColor="neutral.100">
                  <Text fontSize="16px" fontWeight="700" flex="1">대화방</Text>
                  <IconButton size="sm" variant="ghost" aria-label="크게 보기 (대화방 관리로)" icon={<FiMaximize2 />} onClick={() => navigate("/admin/inquiries/chats")} />
                  <IconButton size="sm" variant="ghost" aria-label="접기" icon={<FiMinus />} onClick={collapse} />
                </Flex>
                <HStack px={3} pt={2.5} pb={2} spacing={1.5} flexWrap="wrap">
                  {FILTERS.map(([k, label]) => (
                    <Button key={k} size="xs" borderRadius="full" variant={filter === k ? "solid" : "outline"} colorScheme={filter === k ? "brand" : "gray"}
                      onClick={() => setFilter(k)} aria-pressed={filter === k}>
                      {k === "open" ? `${label} ${rows.length}` : label}
                    </Button>
                  ))}
                </HStack>
                <Box px={3} pb={2.5}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none"><SearchIcon color="neutral.400" boxSize={3} /></InputLeftElement>
                    <Input placeholder="회원 이름 · 이메일" value={q} onChange={(e) => setQ(e.target.value)} bg="neutral.50" aria-label="대화방 검색" />
                  </InputGroup>
                </Box>
                <Box flex="1" overflowY="auto" borderTop="1px solid" borderColor="neutral.100">
                  {loading ? <Flex justify="center" py={8}><Spinner color="brand.500" /></Flex>
                    : visibleRows.length === 0
                      ? <Text p={6} textAlign="center" fontSize="sm" color="neutral.500">
                          {rows.length === 0 ? "열린 대화방이 없어요." : "조건에 맞는 대화방이 없어요."}
                        </Text>
                      : visibleRows.map((r) => <AdminOpsRoomRow key={r.id} r={r} onClick={() => openRoom(r)} />)}
                </Box>
                <Flex px={3} py={2} borderTop="1px solid" borderColor="neutral.100" justify="space-between" align="center" gap={2}>
                  <Text fontSize="12px" color="neutral.600">방 열기·종료는 크게 보기에서</Text>
                  <Button size="xs" variant="ghost" colorScheme="brand" onClick={() => navigate("/admin/inquiries/chats?new=1")}>+ 새 대화방</Button>
                </Flex>
              </>
            )}
        </Flex>
      )}

      {!open && bubble && (
        <Box
          as="button" type="button" onClick={() => openRoom(bubble)}
          position="fixed" right={{ base: "80px", md: "84px" }} bottom={{ base: "22px", md: "26px" }} zIndex={1250}
          w="290px" maxW="calc(100vw - 110px)" textAlign="left" bg="white" border="1px solid" borderColor="neutral.200" borderRadius="12px"
          boxShadow="0 8px 24px rgba(26,25,24,0.14)" px={3.5} py={2.5}
        >
          <Flex justify="space-between" gap={2}>
            <Text fontSize="13px" fontWeight="700" noOfLines={1}>{bubble.name} 님이 답장했어요</Text>
            <Text fontSize="12px" color="neutral.600" flexShrink={0}>방금</Text>
          </Flex>
          {bubble.text && <Text fontSize="13px" color="neutral.700" noOfLines={1}>{bubble.text}</Text>}
        </Box>
      )}

      <Box position="fixed" right={{ base: "16px", md: "20px" }} bottom={{ base: "16px", md: "20px" }} zIndex={1250}>
        <AdminFeatureTip noticeKey="admin_ops_chat_dock_v1" anchor="ops-chat-dock">
        <IconButton
          ref={buttonRef}
          isRound w="52px" h="52px" minW="52px" bg="brand.700" color="white" _hover={{ bg: "brand.800" }} _active={{ bg: "brand.800" }}
          boxShadow="0 6px 18px rgba(23,106,92,0.35)"
          aria-label={open ? "대화창 닫기" : `운영팀 대화방 열기${total ? ` (안 읽은 메시지 ${total})` : ""}`}
          aria-expanded={open}
          icon={open ? <FiX size={22} /> : <FiMessageCircle size={24} />}
          onClick={() => (open ? collapse() : (setOpen(true), setBubble(null)))}
        />
        </AdminFeatureTip>
        {!open && total > 0 && (
          <Badge position="absolute" top="-4px" right="-4px" minW="20px" h="20px" borderRadius="full" bg="status.error" color="white"
            border="2px solid white" fontSize="11px" display="flex" alignItems="center" justifyContent="center" pointerEvents="none" aria-hidden="true">
            {total > 99 ? "99+" : total}
          </Badge>
        )}
      </Box>
    </>
  );
}

function DockRoom({ room, unreadElsewhere, onBack, onBig, onCollapse, onChanged }) {
  const toast = useToast();
  const bottomRef = useRef(null);
  const { thread, error, reload } = useAdminOpsRoom(room.id, {
    errorText: errText,
    onNewMessages: () => setTimeout(() => bottomRef.current?.scrollIntoView?.({ block: "end" }), 0),
  });

  const send = async (body, files) => {
    try {
      await sendMessage(room.id, body, files);
      await reload();
      onChanged?.();
    } catch (err) {
      toast({ title: "보내지 못했어요.", description: errText(err), status: "error" });
      throw err;
    }
  };

  const r = thread?.room;
  const isOpen = r?.status === "open";
  const waiting = isOpen && r?.session_status === "waiting";
  const subline = r
    ? [
      !isOpen ? "종료됨" : waiting ? "연결 대기" : "상담 중",
      r.origin && r.origin !== "admin" ? (OPS_ORIGIN_LABEL[r.origin] || "레이에서 연결") : null,
      `담당 ${r.assignee_name || "없음"}`,
      r.topic_label || null,
    ].filter(Boolean).join(" · ")
    : "";

  return (
    <>
      <Flex px={2} py={2} align="center" gap={1} borderBottom="1px solid" borderColor="neutral.100">
        <Button size="sm" variant="ghost" leftIcon={<FiChevronLeft />} onClick={onBack} px={2} flexShrink={0}
          aria-label={unreadElsewhere ? `목록으로 (다른 방 안 읽은 메시지 ${unreadElsewhere})` : "목록으로"}>
          목록
          {unreadElsewhere > 0 && (
            <Badge ml={1} bg="status.error" color="white" borderRadius="full" px={1.5} fontSize="10px" aria-hidden="true">{unreadElsewhere}</Badge>
          )}
        </Button>
        <Box flex="1" minW={0} pl={1}>
          <Text fontSize="15px" fontWeight="700" noOfLines={1}>{room.name || r?.creator_email || "대화방"}</Text>
          <Text fontSize="11.5px" color="neutral.600" noOfLines={1}>{subline}</Text>
        </Box>
        <IconButton size="sm" variant="ghost" aria-label="크게 보기 (회원 정보·종료·담당자)" icon={<FiMaximize2 />} onClick={onBig} />
        <IconButton size="sm" variant="ghost" aria-label="접기" icon={<FiMinus />} onClick={onCollapse} />
      </Flex>

      <Box flex="1" overflowY="auto" p={3} bg="neutral.50">
        {error ? <Text p={6} textAlign="center" fontSize="sm" color="neutral.500">{error}</Text>
          : !thread ? <Flex justify="center" py={10}><Spinner color="brand.500" /></Flex>
            : (
              <>
                <OpsChatMessages messages={thread.messages} viewer="admin" otherReadAt={thread.other_read_at} laySummary={r.lay_summary} />
                <div ref={bottomRef} />
              </>
            )}
      </Box>

      {thread && (isOpen ? (
        <Box px={3} pt={2} pb={3} borderTop="1px solid" borderColor="neutral.100">
          {waiting && <Text fontSize="12px" color="status.warning" mb={1.5}>첫 답을 보내면 「상담 중」으로 바뀌고 내가 담당이 돼요.</Text>}
          <HStack spacing={1.5} mb={2} flexWrap="wrap">
            {ADMIN_QUICK_REPLIES.map((t) => (
              <Button key={t} size="xs" variant="outline" borderRadius="full" onClick={() => send(t, []).catch(() => {})}>{t}</Button>
            ))}
          </HStack>
          <OpsChatComposer variant="square" enterToSend placeholder="답장 입력 (Enter 보내기 · 캡처는 붙여넣기)"
            onSend={send} onError={(code) => toast({ title: ADMIN_ERROR_TEXT[code] || ADMIN_ERROR_TEXT.unknown, status: "warning" })} />
        </Box>
      ) : (
        <Box px={4} py={3} borderTop="1px solid" borderColor="neutral.100" fontSize="13px" color="neutral.600">
          {r?.closed_at ? `${fmtDateTime(r.closed_at)} 종료된 대화방이에요.` : "종료된 대화방이에요."} 다시 대화하려면 크게 보기에서 새 대화방을 여세요.
        </Box>
      ))}
    </>
  );
}
